/**
 * API entry point.
 *
 * This file is the SINGLE switch between mock and real APIs.
 * All components import from here:
 *
 *   import { authApi, userApi, ... } from '@/services/api';
 *
 * NEVER import directly from `authApi.js` or `authApi.mock.js` —
 * always go through this file so the mock flag works correctly.
 *
 * Controlled by `VITE_USE_MOCKS` env var:
 *   true  → use mocks (default in dev)
 *   false → use real backend
 *
 * Note: Both real and mock modules are bundled. This costs ~5KB in
 * production but keeps the code simple. If you ever need to eliminate
 * mocks from the production bundle, switch to dynamic imports.
 */

import realAuthApi from './authApi';
import mockAuthApi from './authApi.mock';
import realUserApi from './userApi';
import mockUserApi from './userApi.mock';
import realServicesApi from './servicesApi';
import mockServicesApi from './servicesApi.mock';
import realTransactionsApi from './transactionsApi';
import mockTransactionsApi from './transactionsApi.mock';
import realServiceHistoryApi from './serviceHistoryApi';
import mockServiceHistoryApi from './serviceHistoryApi.mock';
import realSupportApi from './supportApi';
import mockSupportApi from './supportApi.mock';
import realClinicTicketsApi from './clinicTicketsApi';
import mockClinicTicketsApi from './clinicTicketsApi.mock';
import realClinicsApi from './clinicsApi';
import realFaceCaptureApi from './faceCaptureApi';
import mockFaceCaptureApi from './faceCaptureApi.mock';
import mockClinicsApi from './clinicsApi.mock';
import realCardsApi from './cardsApi';
import mockCardsApi from './cardsApi.mock';
import realConsultationsApi from './consultationsApi';
import mockConsultationsApi from './consultationsApi.mock';
import realDiscountsApi from './discountsApi';
import mockDiscountsApi from './discountsApi.mock';
import realCandleCardApi from './candleCardApi';
import mockCandleCardApi from './candleCardApi.mock';
import realRerunsApi from './rerunsApi';
import mockRerunsApi from './rerunsApi.mock';
import realNotificationsApi from './notificationsApi';
import mockNotificationsApi from './notificationsApi.mock';

const USE_MOCKS = import.meta.env.VITE_USE_MOCKS === 'true';

if (USE_MOCKS) {
  console.info('🟡 API: Using MOCK implementations (VITE_USE_MOCKS=true)');
} else {
  console.info('🟢 API: Using REAL backend at', import.meta.env.VITE_API_BASE_URL);
}

export const authApi = USE_MOCKS ? mockAuthApi : realAuthApi;
export const userApi = USE_MOCKS ? mockUserApi : realUserApi;
export const servicesApi = USE_MOCKS ? mockServicesApi : realServicesApi;
export const transactionsApi = USE_MOCKS ? mockTransactionsApi : realTransactionsApi;
export const serviceHistoryApi = USE_MOCKS ? mockServiceHistoryApi : realServiceHistoryApi;
export const supportApi = USE_MOCKS ? mockSupportApi : realSupportApi;
export const clinicTicketsApi = USE_MOCKS ? mockClinicTicketsApi : realClinicTicketsApi;
export const clinicsApi = USE_MOCKS ? mockClinicsApi : realClinicsApi;
export const faceCaptureApi = USE_MOCKS ? mockFaceCaptureApi : realFaceCaptureApi;
export const notificationsApi = USE_MOCKS ? mockNotificationsApi : realNotificationsApi;
export const consultationsApi = USE_MOCKS ? mockConsultationsApi : realConsultationsApi;
export const cardsApi = USE_MOCKS ? mockCardsApi : realCardsApi;
export const discountsApi = USE_MOCKS ? mockDiscountsApi : realDiscountsApi;
export const rerunsApi = USE_MOCKS ? mockRerunsApi : realRerunsApi;
export const candleCardApi = USE_MOCKS ? mockCandleCardApi : realCandleCardApi;

export const isMockMode = USE_MOCKS;
